<script setup lang="ts">
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeToggle from "@dsk/components/LeToggle.vue";
import LeBell from "@dsk/icons/LeBell.vue";
import { ref, watch } from "vue";
import type { Composer } from "vue-i18n";

import { MatomoHelper } from "@/helpers";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

const props = defineProps<{
  i18n: Composer;
}>();

const emit = defineEmits<{
  closeToast: [];
  cancelClick: [];
  disableToastClick: [];
}>();

const showToast = ref<boolean>(true);

watch(
  () => showToast.value,
  (value) => {
    MatomoHelper.sendEvent(
      MatomoEventCategory.Search,
      MatomoEventAction.ToggleDictionaryJumpToastSetting,
      "Toggled setting to: " + (value ? "show" : "hide")
    );
  }
);

const onCancelClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ClickDictionaryJumpToastSettingCancel,
    "Clicked cancel button in change dictionary jump toast setting"
  );

  emit("cancelClick");
};

const onConfirmClick = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.SubmitDictionaryJumpToastSetting,
    "Submitted setting with value: " + (showToast.value ? "show" : "hide")
  );

  if (!showToast.value) {
    emit("disableToastClick");
  }

  emit("closeToast");
};
</script>

<template>
  <div class="le-search-switched-dictionary-setting-toast">
    <span class="le-search-switched-dictionary-setting-toast__content">
      <LeBell role="presentation" class="le-search-switched-dictionary-setting-toast__content-icon" />
      {{ props.i18n.t("le-notification.setting.text") }}
      <LeToggle v-model="showToast" class="le-search-switched-dictionary-setting-toast__content-toggle" />
    </span>

    <span class="le-search-switched-dictionary-setting-toast__buttons">
      <button type="button" class="le-search-switched-dictionary-setting-toast__cancel-button" @click="onCancelClick()">
        {{ props.i18n.t("le-notification.cancel.text") }}
      </button>
      <LeButton
        class="le-search-switched-dictionary-setting-toast__submit-button"
        color="attention"
        @click="onConfirmClick()"
      >
        {{ props.i18n.t("le-notification.setting.button") }}
      </LeButton>
    </span>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-search-switched-dictionary-setting-toast {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;

  #media-breakpoint-up(lg, {
    flex-direction: row;
    align-items: center;
    gap: 0;
  });

  &__content {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 12px;

    #media-breakpoint-up(md, {
      justify-content: center;
    });

    #media-breakpoint-up(lg, {
      justify-content: start;
    });

    &-icon,
    &-toggle {
      flex-shrink: 0;
    }
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap-reverse;
    justify-content: start;
    gap: 24px;
    margin-left: 24px;

    #media-breakpoint-up(md, {
      justify-content: center;
    });
  }

  &__cancel-button {
    border: none;
    background: none;
    text-decoration: underline;
    white-space: nowrap;

    &.focus-visible {
      border-radius: 4px;
      .focus-outline(yellow);
    }
  }

  // Ensure the button fits within the toast height
  &__submit-button {
    margin-top: -1px;
    margin-bottom: -1px;
  }
}
</style>
